import { useState } from "react";
import { Heart, MessageCircle, Share2, BadgeCheck, Plus } from "lucide-react";
import { SectionHeading } from "@/components/site/ui";
import { useSeo } from "@/lib/seo";
import { useLead } from "@/context/LeadContext";

const ROOMS = ["Startup Growth", "Digital Marketing", "Influencer Marketing", "Business Networking", "PR & Media", "Women in Business", "Founders Club"];
const SEED_POSTS = [
    { name: "Ananya Verma", role: "PR Strategist", text: "Just helped a D2C founder land their first podcast feature. Visibility compounds — start before you feel ready.", likes: 42, comments: 8 },
    { name: "Karan Singh", role: "Food Creator", text: "Reels tip: the first 1.5 seconds decide everything. Lead with the payoff, not the intro.", likes: 118, comments: 23 },
    { name: "FreshLeaf", role: "D2C Brand", text: "Just completed our first 100 customer milestone. Grateful to this community for the early support! 🙌", likes: 204, comments: 37 },
];

export default function Community() {
    const { openLead } = useLead();
    const [posts, setPosts] = useState(SEED_POSTS);
    const [draft, setDraft] = useState("");
    useSeo({ title: "SuDigi Community — The Community Behind the Brands", description: "Share wins, ask questions and connect with founders, creators and marketers on SuDigi." });

    const post = () => {
        if (!draft.trim()) return;
        setPosts([{ name: "You", role: "Member", text: draft, likes: 0, comments: 0 }, ...posts]);
        setDraft("");
    };
    const like = (i) => setPosts((p) => p.map((x, j) => j === i ? { ...x, likes: x.likes + 1 } : x));

    return (
        <section className="bg-teal-900 py-14 min-h-[80vh]">
            <div className="container-x grid lg:grid-cols-4 gap-8">
                <aside className="lg:col-span-1">
                    <h3 className="text-cream font-semibold mb-3">Discussion Rooms</h3>
                    <div className="space-y-2">{ROOMS.map((r) => <div key={r} className="card-teal rounded-xl px-4 py-2.5 text-sm text-cream-soft">{r}</div>)}</div>
                </aside>
                <div className="lg:col-span-3">
                    <div className="text-center flex flex-col items-center mb-8"><SectionHeading center eyebrow="Community" title="The Community Behind the Brands" /></div>
                    <div className="card-teal rounded-2xl p-4 mb-6">
                        <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={2} placeholder="Share a win, ask a question…" className="w-full bg-teal-800/50 border border-brandborder rounded-xl p-3 text-cream text-sm" data-testid="community-post-input" />
                        <div className="flex justify-end mt-2"><button onClick={post} className="btn-gold rounded-full px-5 py-2 text-sm flex items-center gap-1.5" data-testid="community-post-btn"><Plus className="w-4 h-4" /> Post</button></div>
                    </div>
                    <div className="space-y-4">
                        {posts.map((p, i) => (
                            <div key={i} className="card-teal rounded-2xl p-5" data-testid={`community-post-${i}`}>
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-teal-700 text-gold grid place-items-center font-serif">{p.name.charAt(0)}</div>
                                    <div><div className="flex items-center gap-1 text-cream font-semibold text-sm">{p.name} <BadgeCheck className="w-3.5 h-3.5 text-growth" /></div><div className="text-xs text-cream-soft/60">{p.role}</div></div>
                                </div>
                                <p className="mt-3 text-cream-soft text-sm">{p.text}</p>
                                <div className="flex gap-6 mt-4 text-cream-soft/70 text-sm">
                                    <button onClick={() => like(i)} className="flex items-center gap-1.5 hover:text-gold" data-testid={`community-like-${i}`}><Heart className="w-4 h-4" /> {p.likes}</button>
                                    <span className="flex items-center gap-1.5"><MessageCircle className="w-4 h-4" /> {p.comments}</span>
                                    <button onClick={() => openLead("Community")} className="flex items-center gap-1.5 hover:text-gold"><Share2 className="w-4 h-4" /> Share</button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
